import * as React from 'react';

import { cn, Notes, Row } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { FieldFocusIndicator } from '~/shared/components/FieldFocusIndicator';

export interface NotesConfig {
  primaryTitle: string;
  primarySubtitle: string;
  primaryPlaceholder: string;
  secondaryTitle: string;
  secondarySubtitle: string;
  secondaryPlaceholder: string;

  primaryField: string;
  secondaryField: string;
}

export interface NotesTabProps<T> {
  entity: T;
  isReadonly: boolean;
  config: NotesConfig;
  onUpdateNotes: (updates: { [key: string]: string }) => Promise<void>;
  onNotesChange?: (updates: { [key: string]: string }) => void;
  initialPrimaryNotes?: string;
  initialSecondaryNotes?: string;
  className?: string;
}

//
// ⚛️ ⚛️ ⚛️ NOTES TAB ⚛️ ⚛️ ⚛️
//
export function NotesTab<T extends Record<string, unknown>>({
  entity,
  isReadonly,
  config,
  onUpdateNotes,
  onNotesChange,
  initialPrimaryNotes,
  initialSecondaryNotes,
  className,
}: NotesTabProps<T>) {
  const t = useTranslations();

  const setFocusedField = useSetAtom($focusedField);
  const focusPositions = useAtomValue($remoteFocusPositions);

  // Refs
  const lastSavedPrimary = React.useRef(
    getInitialValue(initialPrimaryNotes, entity, config.primaryField),
  );
  const lastSavedSecondary = React.useRef(
    getInitialValue(initialSecondaryNotes, entity, config.secondaryField),
  );
  const onUpdateNotesRef = React.useRef(onUpdateNotes);
  onUpdateNotesRef.current = onUpdateNotes;
  const primaryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const secondaryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // States
  const [primaryNotes, setPrimaryNotes] = React.useState(() =>
    getInitialValue(initialPrimaryNotes, entity, config.primaryField),
  );
  const [secondaryNotes, setSecondaryNotes] = React.useState(() =>
    getInitialValue(initialSecondaryNotes, entity, config.secondaryField),
  );
  const [primaryLoading, setPrimaryLoading] = React.useState(false);
  const [secondaryLoading, setSecondaryLoading] = React.useState(false);
  const [primarySuccess, setPrimarySuccess] = React.useState<
    boolean | undefined
  >(undefined);
  const [secondarySuccess, setSecondarySuccess] = React.useState<
    boolean | undefined
  >(undefined);

  // Memoized values
  const notesLabels = React.useMemo(
    () => ({
      saving: t.bookingDetail.notes.states.saving,
      saved: t.bookingDetail.notes.states.saved,
      failed: t.bookingDetail.notes.states.failed,
      noNotesAvailable: t.bookingDetail.notes.states.noNotesAvailable,
      clearAll: t.bookingDetail.notes.states.clearAll,
    }),
    [t],
  );

  // Callbacks
  const schedulePrimarySave = React.useCallback(
    (value: string) => {
      if (primaryTimerRef.current) clearTimeout(primaryTimerRef.current);
      primaryTimerRef.current = setTimeout(async () => {
        if (value === lastSavedPrimary.current) return;
        try {
          setPrimaryLoading(true);
          setPrimarySuccess(undefined);
          await onUpdateNotesRef.current({
            [config.primaryField]: value,
          });
          setPrimarySuccess(true);
          lastSavedPrimary.current = value;
        } catch (error) {
          console.error(`Failed to update ${config.primaryField}:`, error);
          setPrimarySuccess(false);
          setPrimaryNotes(lastSavedPrimary.current);
        } finally {
          setPrimaryLoading(false);
        }
      }, 500);
    },
    [config.primaryField],
  );

  const scheduleSecondarySave = React.useCallback(
    (value: string) => {
      if (secondaryTimerRef.current) clearTimeout(secondaryTimerRef.current);
      secondaryTimerRef.current = setTimeout(async () => {
        if (value === lastSavedSecondary.current) return;
        try {
          setSecondaryLoading(true);
          setSecondarySuccess(undefined);
          await onUpdateNotesRef.current({
            [config.secondaryField]: value,
          });
          setSecondarySuccess(true);
          lastSavedSecondary.current = value;
        } catch (error) {
          console.error(`Failed to update ${config.secondaryField}:`, error);
          setSecondarySuccess(false);
          setSecondaryNotes(lastSavedSecondary.current);
        } finally {
          setSecondaryLoading(false);
        }
      }, 500);
    },
    [config.secondaryField],
  );

  const onPrimaryNotesChange = React.useCallback(
    (value: string) => {
      setPrimaryNotes(value);
      onNotesChange?.({ [config.primaryField]: value });
      if (!isReadonly) schedulePrimarySave(value);
    },
    [onNotesChange, config.primaryField, isReadonly, schedulePrimarySave],
  );

  const onSecondaryNotesChange = React.useCallback(
    (value: string) => {
      setSecondaryNotes(value);
      onNotesChange?.({ [config.secondaryField]: value });
      if (!isReadonly) scheduleSecondarySave(value);
    },
    [onNotesChange, config.secondaryField, isReadonly, scheduleSecondarySave],
  );

  const onPrimaryNotesClear = React.useCallback(() => {
    onPrimaryNotesChange('');
  }, [onPrimaryNotesChange]);

  const onSecondaryNotesClear = React.useCallback(() => {
    onSecondaryNotesChange('');
  }, [onSecondaryNotesChange]);

  /** Sync local state when the upstream form values change when switching ticket */
  React.useEffect(() => {
    const nextPrimary = getInitialValue(
      initialPrimaryNotes,
      entity,
      config.primaryField,
    );
    const nextSecondary = getInitialValue(
      initialSecondaryNotes,
      entity,
      config.secondaryField,
    );
    setPrimaryNotes(nextPrimary);
    setSecondaryNotes(nextSecondary);
    lastSavedPrimary.current = nextPrimary;
    lastSavedSecondary.current = nextSecondary;
  }, [
    initialPrimaryNotes,
    initialSecondaryNotes,
    entity,
    config.primaryField,
    config.secondaryField,
  ]);

  /** Clean up pending timers on unmount */
  React.useEffect(
    () => () => {
      if (primaryTimerRef.current) clearTimeout(primaryTimerRef.current);
      if (secondaryTimerRef.current) clearTimeout(secondaryTimerRef.current);
    },
    [],
  );

  return (
    <div className={cn('@NotesTab', 'h-full p-5', className)}>
      <Row gap2 className="h-full flex-1">
        <FieldFocusIndicator
          fieldId={config.primaryField}
          focusPositions={focusPositions}
          className="flex-1"
        >
          <div
            onFocus={() => setFocusedField(config.primaryField)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setFocusedField(null);
              }
            }}
            className="h-full"
          >
            <Notes
              title={config.primaryTitle}
              subtitle={config.primarySubtitle}
              placeholder={config.primaryPlaceholder}
              value={primaryNotes}
              onChange={onPrimaryNotesChange}
              onClear={onPrimaryNotesClear}
              loading={primaryLoading}
              success={primarySuccess}
              readOnly={isReadonly}
              labels={notesLabels}
            />
          </div>
        </FieldFocusIndicator>
        <FieldFocusIndicator
          fieldId={config.secondaryField}
          focusPositions={focusPositions}
          className="flex-1"
        >
          <div
            onFocus={() => setFocusedField(config.secondaryField)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setFocusedField(null);
              }
            }}
            className="h-full"
          >
            <Notes
              title={config.secondaryTitle}
              subtitle={config.secondarySubtitle}
              value={secondaryNotes}
              placeholder={config.secondaryPlaceholder}
              onChange={onSecondaryNotesChange}
              onClear={onSecondaryNotesClear}
              loading={secondaryLoading}
              success={secondarySuccess}
              readOnly={isReadonly}
              labels={notesLabels}
            />
          </div>
        </FieldFocusIndicator>
      </Row>
    </div>
  );
}

function getInitialValue<T extends Record<string, unknown>>(
  initial: string | undefined,
  entity: T,
  field: string,
): string {
  return initial ?? (entity[field] as string) ?? '';
}
